import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import StatCard from './StatCard.js';
import './Overview.css';

const SYSTEM_ACCOUNT = "000000000000000000000000";

const formatLotId = (lotId, prefix) => {
  return `${prefix}-${String(lotId).padStart(4, '0')}`;
};

const mmToCm = (number) => {
  if (number >= 100) {
    number = Math.round(number / 10);
    return `${number}cm`;
  }
  return `${number}mm`;
}

export const Overview = ({ navigateTo }) => {
  const { t, i18n } = useTranslation();
  const [herdCount, setHerdCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [breedCount, setBreedCount] = useState(0);
  const [bullCount, setBullCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [areaCount, setAreaCount] = useState(0);
  const [activeLots, setActiveLots] = useState([]);
  const [sexNotSetCount, setSexNotSetCount] = useState(0);
  const [weightLotsCount, setWeightLotsCount] = useState(0);
  const [monthlyRain, setMonthlyRain] = useState(0);
  const [financeThisMonth, setFinanceThisMonth] = useState({ count: 0, net: 0 });
  const [listCount, setListCount] = useState(0);

  // Get current month in YYYY-MM format for rain query
  const now = new Date();
  const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthName = now.toLocaleDateString(i18n.language || 'en-US', { month: 'long' });

  useEffect(() => {
    // Fetch herd count from lots.animals (enrolled animals in herd lots) —
    // same response also drives the "sex not set" data-quality nudge below,
    // scoped to alive animals only (a dead animal's missing sex doesn't
    // block anything functionally, unlike a living one that can't be added
    // to a reproduction lot until it's fixed).
    Fetch("api/herd/animals", "get", null,
      (data) => {
        setHerdCount(data.length);
        setSexNotSetCount(data.filter(a => a.is_alive !== false && !a.sex).length);
      },
      () => console.log("error loading herd")
    );

    // Fetch team count
    Fetch("api/data/teams", "get", null,
      (data) => setTeamCount(data.length),
      () => console.log("error loading teams")
    );

    // Fetch breeds and count only custom ones
    Fetch("api/data/breeds", "get", null,
      (data) => {
        const customBreeds = data.filter(b => b.account !== SYSTEM_ACCOUNT);
        setBreedCount(customBreeds.length);
      },
      () => console.log("error loading breeds")
    );

    // Fetch bulls count
    Fetch("api/data/reproduction.bulls", "get", null,
      (data) => setBullCount(data.length),
      () => console.log("error loading bulls")
    );

    // Fetch messages count
    Fetch("api/data/messages", "get", null,
      (data) => setMessageCount(data.length),
      () => console.log("error loading messages")
    );

    // Fetch area count
    Fetch("api/data/areas", "get", null,
      (data) => setAreaCount(data.length),
      () => console.log("error loading messages")
    );

    // Fetch Active Reproduction Lots — status is now authoritative (set by
    // the daily archiveCompletedReproductionLots job once a protocol's
    // timeline has run its course), so no client-side date math needed.
    Fetch("api/data/lots?search_fields=type,status&search_values=reproduction,active", "get", null,
      (data) => setActiveLots(data || []),
      () => console.log("error loading lots")
    );

    // Fetch weight lots count
    Fetch("api/data/lots?search_fields=type&search_values=weight", "get", null,
      (data) => setWeightLotsCount((data || []).length),
      () => {}
    );

    // Fetch rain for current month and sum amounts
    Fetch(`api/data/rain?search_fields=date&search_values=^${currentYearMonth}`, "get", null,
      (data) => {
        const total = data.reduce((sum, record) => sum + (record.amount || 0), 0);
        setMonthlyRain(total);
      },
      () => console.log("error loading rain")
    );

    Fetch("api/data/lists.lists?search_fields=status&search_values=active", "get", null,
      (data) => setListCount((data || []).length),
      () => {}
    );

    Fetch(`api/data/finance.transactions?search_fields=date&search_values=^${currentYearMonth}`, "get", null,
      (data) => {
        let net = 0;
        data.forEach(row => {
          if (row.type === 'gain') net += Number(row.amount || 0);
          else net -= Number(row.amount || 0);
        });
        setFinanceThisMonth({ count: data.length, net: Math.round(net * 100) / 100 });
      },
      () => {}
    );
  }, [currentYearMonth]);

  const alertCards = [
    sexNotSetCount > 0 && {
      key: 'sexNotSet',
      title: t("animals_sex_not_set"),
      value: sexNotSetCount,
      color: "#c0392b",
      onClick: () => navigateTo && navigateTo('herdSexNotSet'),
    },
  ].filter(Boolean);

  return (
    <div className="Overview">
      <div className="stat-grid">
        <StatCard
          title={t("total_herd")}
          value={herdCount}
          color="#b8862f"
        />
        <StatCard
          title={t("total_areas")}
          value={areaCount}
          color="#383F51"
          onAction={() => navigateTo && navigateTo('addArea')}
        />
        <StatCard
          title={t("total_custom_breeds")}
          value={breedCount}
          color="#8e44ad"
          onAction={() => navigateTo && navigateTo('addBreed')}
        />
        <StatCard
          title={t("total_bulls")}
          value={bullCount}
          color="#6B4423"
          onAction={() => navigateTo && navigateTo('addBull')}
        />
        <StatCard
          title={t("total_team_members")}
          value={teamCount}
          color="#2980b9"
          onAction={() => navigateTo && navigateTo('addTeam')}
          actionThreshold={1}
        />
        <StatCard
          title={t("whatsapp_usage")}
          value={messageCount}
          color="#A26769"
          onAction={() => navigateTo && navigateTo('whatsapp')}
        />
        <StatCard
          title={t("active_reproduction_lots")}
          value={
            activeLots.length === 0
              ? t("none")
              : activeLots.length === 1
                ? formatLotId(activeLots[0].lot_id, t('lot_prefix'))
                : activeLots.length
          }
          color="#582C4D"
          smallValue={activeLots.length === 1}
        />
        <StatCard
          title={t("active_weight_lots")}
          value={weightLotsCount === 0 ? t("none") : weightLotsCount}
          color="#b8862f"
        />
        <StatCard
          title={`${currentMonthName} ${t("rain")}`}
          value={mmToCm(monthlyRain)}
          color="#3498db"
        />
        <StatCard
          title={t('transactions_this_month')}
          value={financeThisMonth.count === 0 ? '—' : `${financeThisMonth.count}`}
          color="#b8862f"
          smallValue={false}
        />
        <StatCard
          title={t('active_lists')}
          value={listCount}
          color="#b8862f"
          onAction={() => navigateTo && navigateTo('addList')}
        />
      </div>

      {/* Data-quality nudges — each entry only appears once its count is
          actually > 0, and the whole section disappears when none are.
          Sex is the first one; more fields (birth date, breed, etc.) are
          expected to join this same array later rather than getting their
          own bespoke section each time. */}
      {alertCards.length > 0 && (
        <div className="overview-alerts">
          <div className="overview-section-header">
            <span className="overview-section-label">{t('needs_attention')}</span>
          </div>
          <div className="stat-grid">
            {alertCards.map(card => <StatCard key={card.key} {...card} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default Overview;
